import { ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { BlotterRequestByBlotterType } from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { connect } from 'react-redux';
import { blotterPanelItemReset } from 'state/blotters/blotterPanel/blotterPanel.action';
import type { ActivityFilter, DateFilter, PlatformFilter, ProductFilter } from 'state/blotters/common/blotter.model';
import {
  TradesBlottersFilterChanged,
  tradesBlottersAdvancedFiltersToggled,
  tradesBlottersFilterChanged,
  tradesBlottersFilterEnabled,
  tradesBlottersManualRefreshRequested,
} from 'state/blotters/trades/tradesBlotters.actions';
import {
  selectIsEndDateInThePast,
  selectTradesBlotterRequestByBlotterType,
  selectTradesBlottersError,
  selectTradesBlottersFiltersByBlotterType,
  selectTradesBlottersLoading,
  selectTradesBlottersType,
} from 'state/blotters/trades/tradesBlotters.selector';
import { BlotterType, FilterBlotterType, TradesOrProductsBlotterType } from 'state/blotters/trades/types/types';
import { getIsUserInternal } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

export type FilterOwnProps = {
  lastBlotterRequest: ServerSideBlotterSearchRequest | undefined;
};

interface CommonStateProps<T extends BlotterType> {
  quickSearchTerm: string;
  activityFilter: ActivityFilter;
  dateFilter: DateFilter;
  startDate?: string;
  endDate?: string;

  advancedFilters: boolean;

  requestParams: BlotterRequestByBlotterType<T>;
  loading: boolean;
  error: boolean;
  isInternal: boolean;
  blotterTypeFilter: T;
}
interface TradesOrProductsFilterState extends CommonStateProps<TradesOrProductsBlotterType> {
  productFilter: ProductFilter;
  platformFilter: PlatformFilter;
  currencyFilter: string;
  isEndDateInThePast: boolean;
}

export type FilterStateProps = TradesOrProductsFilterState | CommonStateProps<'dlpProducts'>;

type CommonFilterDispatchProps = {
  onFullTextSearchChanged<T extends FilterBlotterType>(blotter: T, value?: string): void;
  onActivityFilterChanged<T extends FilterBlotterType>(blotter: T, value: ActivityFilter): void;
  onBlotterTypeFilterChanged<T extends FilterBlotterType>(blotter: T): void;
  onManualRefresh<T extends FilterBlotterType>(blotter: T): void;

  onDateFilterChanged<T extends FilterBlotterType>(blotter: T, value: DateFilter): void;
  onStartDateFilterChanged<T extends FilterBlotterType>(blotter: T, date: Nullable<string>): void;
  onEndDateFilterChanged<T extends FilterBlotterType>(blotter: T, date: Nullable<string>): void;
  onAdvancedFilterToggled<T extends FilterBlotterType>(blotter: T): void;
};

interface TradesOrProductsFilterDispatchProps extends CommonFilterDispatchProps {
  onProductFilterChanged<T extends TradesOrProductsBlotterType>(blotter: T, product: ProductFilter): void;
  onPlatformFilterChanged<T extends TradesOrProductsBlotterType>(blotter: T, platform: PlatformFilter): void;
  onCurrencyFilterChanged<T extends TradesOrProductsBlotterType>(blotter: T, value: string): void;
}

export type FilterDispatchProps = CommonFilterDispatchProps | TradesOrProductsFilterDispatchProps;

export type TradesOrProductsFilterProps = FilterOwnProps &
  TradesOrProductsFilterState &
  TradesOrProductsFilterDispatchProps;
export type DlpProductsFilterProps = FilterOwnProps & CommonStateProps<'dlpProducts'> & CommonFilterDispatchProps;

export type TradesBlottersFilterProps = TradesOrProductsFilterProps | DlpProductsFilterProps;

export const mapStateToProps = (state: AppState): FilterStateProps => {
  const blotterTypeFilter = selectTradesBlottersType(state);

  const filters = selectTradesBlottersFiltersByBlotterType(state, blotterTypeFilter);
  return {
    ...filters,
    blotterTypeFilter,
    isInternal: getIsUserInternal(state),
    requestParams: selectTradesBlotterRequestByBlotterType(state, blotterTypeFilter),
    loading: selectTradesBlottersLoading(state, blotterTypeFilter, undefined),
    error: selectTradesBlottersError(state, blotterTypeFilter, undefined),
    isEndDateInThePast: selectIsEndDateInThePast(state, blotterTypeFilter),
  } as FilterStateProps;
};

export const mapDispatchToProps = (dispatch: ThunkDispatch): FilterDispatchProps => ({
  onBlotterTypeFilterChanged(blotter) {
    dispatch(tradesBlottersFilterEnabled(blotter));
    dispatch(blotterPanelItemReset());
  },
  onFullTextSearchChanged(blotter, value) {
    dispatch(tradesBlottersFilterChanged(blotter, 'quickSearchTerm', value || ''));
  },
  onDateFilterChanged(blotter, dateFilter) {
    dispatch(
      tradesBlottersFilterChanged<FilterBlotterType, 'dateFilter'>(
        blotter,
        'dateFilter',
        dateFilter,
      ) as unknown as TradesBlottersFilterChanged,
    );
  },
  onActivityFilterChanged(blotter, activity) {
    dispatch(tradesBlottersFilterChanged(blotter, 'activityFilter', activity));
  },
  onProductFilterChanged(blotter, product) {
    dispatch(
      tradesBlottersFilterChanged<TradesOrProductsBlotterType, 'productFilter'>(
        blotter,
        'productFilter',
        product,
      ) as unknown as TradesBlottersFilterChanged,
    );
  },
  onPlatformFilterChanged(blotter, platform) {
    dispatch(
      tradesBlottersFilterChanged<TradesOrProductsBlotterType, 'platformFilter'>(
        blotter,
        'platformFilter',
        platform,
      ) as unknown as TradesBlottersFilterChanged,
    );
  },
  onAdvancedFilterToggled(blotter) {
    dispatch(tradesBlottersAdvancedFiltersToggled(blotter));
  },
  onCurrencyFilterChanged(blotter, currency) {
    dispatch(
      tradesBlottersFilterChanged<TradesOrProductsBlotterType, 'currencyFilter'>(
        blotter,
        'currencyFilter',
        currency,
      ) as unknown as TradesBlottersFilterChanged,
    );
  },
  onStartDateFilterChanged(blotter, date) {
    if (date) {
      dispatch(
        tradesBlottersFilterChanged<FilterBlotterType, 'startDate'>(
          blotter,
          'startDate',
          date,
        ) as unknown as TradesBlottersFilterChanged,
      );
    }
  },
  onEndDateFilterChanged(blotter, date) {
    if (date) {
      dispatch(
        tradesBlottersFilterChanged<FilterBlotterType, 'endDate'>(
          blotter,
          'endDate',
          date,
        ) as unknown as TradesBlottersFilterChanged,
      );
    }
  },
  onManualRefresh(blotter) {
    dispatch(tradesBlottersManualRefreshRequested(blotter));
  },
});

export const connectFilterComponent = connect(mapStateToProps, mapDispatchToProps);
